import React from "react";
import {
    Box, Heading, Text, VStack, HStack, Badge, Icon, Flex,
    Table, Thead, Tbody, Tr, Th, Td, SimpleGrid
} from "@chakra-ui/react";
import { FiCreditCard, FiCheckCircle, FiAlertCircle, FiClock } from "react-icons/fi";
import { formatCurrency, isSupersededInvoice } from "../utils/formatHelpers";
import { PageHeader, PdfActionIcons, getPaymentStatusBadgeProps } from "./portalUi";
import { COLORS } from "../styles/colors";
import moment from "moment";

const getPaymentStatusStyle = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "succeeded" || s === "paid" || s === "completed") return { scheme: "green", label: "Paid" };
    if (s === "failed" || s === "declined") return { scheme: "red", label: "Failed" };
    if (s === "refunded") return { scheme: "purple", label: "Refunded" };
    if (s === "pending" || s === "processing") return { scheme: "orange", label: "Pending" };
    return { scheme: "gray", label: status || "Unknown" };
};

const getStatusIcon = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "succeeded" || s === "paid" || s === "completed") return FiCheckCircle;
    if (s === "failed" || s === "declined") return FiAlertCircle;
    return FiClock;
};

const getPaymentDownloadFilename = (payment) => {
    const label = payment.receipt_number || payment.id;
    return `receipt_${String(label).replace(/\s+/g, "_")}.pdf`;
};

const hasPaymentReceipt = (payment) => !!(payment.pdf_url || payment.pdf_file_id);

const formatPaymentMethod = (method) => {
    if (!method) return "—";
    return method
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase());
};

const formatReceivedAt = (ts) => {
    if (!ts) return "—";
    const m = moment(ts);
    return m.isValid() ? m.format("DD MMM YYYY, h:mm A") : "—";
};

const getInvoiceTotal = (invoice) => {
    const subtotal = invoice.invoice_line_items?.length > 0
        ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0) : 0;
    const gst = invoice.gst_amount || 0;
    return subtotal + gst;
};

export const PaymentsTab = ({ certifications, payments, invoices, standaloneCourses = [] }) => {
    const allPayments = Object.values(payments).flat();
    const totalPaid = allPayments
        .filter(p => ["succeeded", "paid", "completed"].includes((p.status || "").toLowerCase()))
        .reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const totalInvoiced = Object.values(invoices || {})
        .flat()
        .filter((inv) => !isSupersededInvoice(inv))
        .reduce((sum, inv) => sum + getInvoiceTotal(inv), 0);
    const remainingAmount = Math.max(0, totalInvoiced - totalPaid);

    const renderPaymentGroup = (key, title, groupPayments) => {
        if (!groupPayments || groupPayments.length === 0) return null;

        return (
            <Box
                key={key}
                p={{ base: 4, md: 5 }}
                bg="white"
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
            >
                <HStack spacing={2} mb={{ base: 3, md: 4 }}>
                    <Icon as={FiCreditCard} color="blue.500" boxSize={5} flexShrink={0} />
                    <Heading size={{ base: "xs", md: "sm" }} color={COLORS.maastrichtBlue}>
                        {title}
                    </Heading>
                </HStack>

                {/* Mobile Card View */}
                <VStack spacing={2.5} align="stretch" display={{ base: "flex", md: "none" }}>
                    {groupPayments.map((payment) => {
                        const statusStyle = getPaymentStatusStyle(payment.status);
                        const isPaid = statusStyle.scheme === "green";
                        const hasReceipt = hasPaymentReceipt(payment);
                        const StatusIcon = getStatusIcon(payment.status);
                        return (
                            <Box
                                key={payment.id}
                                bg="gray.50"
                                p={3}
                                borderRadius="xl"
                                border="1px solid"
                                borderColor={isPaid ? "green.200" : "gray.200"}
                                borderLeft={isPaid ? "3px solid" : "1px solid"}
                                borderLeftColor={isPaid ? "green.400" : "gray.200"}
                                transition="all 0.2s"
                            >
                                <Flex justify="space-between" align="center" mb={2}>
                                    <HStack spacing={2} flex={1} minW={0}>
                                        <Icon as={StatusIcon} color={`${statusStyle.scheme}.500`} boxSize={5} flexShrink={0} />
                                        <Text fontWeight="bold" fontSize="sm" color="gray.800" noOfLines={1}>
                                            {payment.receipt_number || "—"}
                                        </Text>
                                        <Badge {...getPaymentStatusBadgeProps(statusStyle.scheme)}>
                                            {statusStyle.label}
                                        </Badge>
                                    </HStack>
                                    <Text fontWeight="bold" fontSize="md" color={isPaid ? "green.600" : "gray.800"}>
                                        {formatCurrency(payment.amount)} {payment.currency || "SGD"}
                                    </Text>
                                </Flex>
                                <VStack align="stretch" spacing={1.5}>
                                    <Flex justify="space-between">
                                        <Text fontSize="2xs" color="gray.500" fontWeight="medium">Payment Method</Text>
                                        <Text fontSize="xs" color="gray.700">{formatPaymentMethod(payment.payment_method)}</Text>
                                    </Flex>
                                    <Flex justify="space-between">
                                        <Text fontSize="2xs" color="gray.500" fontWeight="medium">Payment Received On</Text>
                                        <Text fontSize="xs" color="gray.700">{formatReceivedAt(payment.created_at)}</Text>
                                    </Flex>
                                    {payment.notes && (
                                        <Flex justify="space-between">
                                            <Text fontSize="2xs" color="gray.500" fontWeight="medium">Notes</Text>
                                            <Text fontSize="xs" color="gray.700" noOfLines={1} textAlign="right" maxW="60%">{payment.notes}</Text>
                                        </Flex>
                                    )}
                                    {hasReceipt && (
                                        <Flex justify="flex-end" pt={1}>
                                            <PdfActionIcons
                                                item={payment}
                                                viewLabel="View receipt"
                                                downloadLabel="Download receipt"
                                                getDownloadFilename={getPaymentDownloadFilename}
                                                justify="flex-end"
                                            />
                                        </Flex>
                                    )}
                                </VStack>
                            </Box>
                        );
                    })}
                </VStack>

                {/* Desktop Table View */}
                <Box overflowX="auto" w="100%" display={{ base: "none", md: "block" }} borderRadius="xl" border="1px solid" borderColor="gray.200">
                    <Table variant="simple" size="sm" bg="white">
                        <Thead bg="gray.50">
                            <Tr>
                                <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200">Receipt No.</Th>
                                <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200">Method</Th>
                                <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200" isNumeric>Amount</Th>
                                <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200">Status</Th>
                                <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200">Payment Received On</Th>
                                <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200">Notes</Th>
                                <Th color="gray.500" fontWeight="600" fontSize="2xs" textTransform="uppercase" letterSpacing="wider" py={3} borderColor="gray.200">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {groupPayments.map((payment) => {
                                const statusStyle = getPaymentStatusStyle(payment.status);
                                const isPaid = statusStyle.scheme === "green";
                                return (
                                    <Tr
                                        key={payment.id}
                                        bg={isPaid ? "green.50" : "white"}
                                        _hover={{ bg: isPaid ? "green.100" : "gray.50" }}
                                        borderLeft={isPaid ? "3px solid" : "none"}
                                        borderColor={isPaid ? "green.400" : "transparent"}
                                        transition="all 0.15s"
                                    >
                                        <Td>
                                            <Text fontWeight="600" fontSize="sm" color="gray.800">
                                                {payment.receipt_number || <Text as="span" color="gray.400">—</Text>}
                                            </Text>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm" color="gray.700">{formatPaymentMethod(payment.payment_method)}</Text>
                                        </Td>
                                        <Td isNumeric>
                                            <Text fontWeight="700" color={isPaid ? "green.600" : "gray.800"}>
                                                {formatCurrency(payment.amount)}
                                            </Text>
                                            {payment.currency && payment.currency !== "SGD" && (
                                                <Text fontSize="xs" color="gray.400">{payment.currency}</Text>
                                            )}
                                        </Td>
                                        <Td>
                                            <Badge {...getPaymentStatusBadgeProps(statusStyle.scheme)}>
                                                {statusStyle.label}
                                            </Badge>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm" color="gray.700">{formatReceivedAt(payment.created_at)}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm" color="gray.600" noOfLines={1} maxW="160px">
                                                {payment.notes || <Text as="span" color="gray.400">—</Text>}
                                            </Text>
                                        </Td>
                                        <Td>
                                            <PdfActionIcons
                                                item={payment}
                                                viewLabel="View receipt"
                                                downloadLabel="Download receipt"
                                                getDownloadFilename={getPaymentDownloadFilename}
                                            />
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            </Box>
        );
    };

    if (allPayments.length === 0) {
        return (
            <VStack spacing={{ base: 4, md: 5 }} align="stretch">
                <PageHeader
                    icon={FiCreditCard}
                    iconColor="blue.500"
                    title="Payments"
                    subtitle="Transaction history and receipts"
                />
                <Box p={6} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" textAlign="center">
                    <VStack spacing={3}>
                        <Icon as={FiCreditCard} boxSize={10} color="gray.300" />
                        <Text color="gray.500" fontSize="sm" fontWeight="500">No payment records found.</Text>
                    </VStack>
                </Box>
            </VStack>
        );
    }

    return (
        <VStack spacing={{ base: 4, md: 5 }} align="stretch">
            <PageHeader
                icon={FiCreditCard}
                iconColor="blue.500"
                title="Payments"
                subtitle="Transaction history and receipts"
            />

            {/* Summary bar */}
            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={{ base: 2.5, md: 4 }}>
                <Box
                    p={{ base: 3, md: 4 }}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderLeftWidth="3px"
                    borderLeftColor="green.400"
                    bg="white"
                >
                    <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="800" color="green.600" lineHeight="1">
                        {formatCurrency(totalPaid)}
                    </Text>
                    <Text fontSize={{ base: "2xs", md: "sm" }} color="gray.600" mt={1} fontWeight="500">Total Paid</Text>
                </Box>
                <Box
                    p={{ base: 3, md: 4 }}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderLeftWidth="3px"
                    borderLeftColor="orange.400"
                    bg="white"
                >
                    <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="800" color={remainingAmount > 0 ? "orange.600" : "green.600"} lineHeight="1">
                        {formatCurrency(remainingAmount)}
                    </Text>
                    <Text fontSize={{ base: "2xs", md: "sm" }} color="gray.600" mt={1} fontWeight="500">Remaining Amount</Text>
                </Box>
                <Box
                    p={{ base: 3, md: 4 }}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderLeftWidth="3px"
                    borderLeftColor="blue.400"
                    bg="white"
                >
                    <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="800" color="blue.600" lineHeight="1">
                        {allPayments.length}
                    </Text>
                    <Text fontSize={{ base: "2xs", md: "sm" }} color="gray.600" mt={1} fontWeight="500">Total Transactions</Text>
                </Box>
            </SimpleGrid>

            {certifications.map((cert) =>
                renderPaymentGroup(
                    cert.id,
                    cert.full_certification?.name || cert.run_name || cert.id,
                    payments[cert.id] || []
                )
            )}

            {standaloneCourses.map((course) =>
                renderPaymentGroup(
                    course.id,
                    course.course_type?.name || course.course_type_code || course.id,
                    payments[course.id] || []
                )
            )}
        </VStack>
    );
};
